import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  ArrowLeft,
  ArrowRight,
  Mail,
} from "lucide-react";
import { api } from "../lib/api";
import { trackPurchase } from "../lib/tracking";
import Header from "../components/Header";
import Footer from "../components/Footer";

/**
 * Post-payment landing pages for the Netopia online_payment flow.
 *
 * Renders in three variants (one route each):
 *  - /payment/success → polls /api/payments/status/{order_id} until the
 *    IPN flips the purchase to "paid". Pending state shown for up to 30s,
 *    then a soft "Confirmăm plata în câteva minute" copy.
 *  - /payment/cancel → user aborted on Netopia's hosted page.
 *  - /payment/failed → Netopia returned a non-paid status.
 *
 * Important: this page NEVER trusts the URL alone — the authoritative
 * payment outcome always comes from the backend, which itself is driven by
 * the server-to-server IPN. The success URL is treated as a "hint, but
 * verify" signal.
 */

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 20; // 20 × 2s = 40s max wait
const PURCHASE_TRACK_KEY = (id) => `_evt_purchase_${id}`;

function PaymentLayout({ children }) {
  return (
    // M913B — Container intentionally NOT masked. PaymentReturn only
    // shows status copy + CTAs (no order_id / approval code on screen).
    // Keeping it visible lets Clarity surface UX issues on pending/failed
    // states. If the page ever renders a transaction id, wrap that
    // specific element with `clarity-mask` instead.
    <div className="min-h-screen bg-[#F5F6F6] flex flex-col">
      <Header />
      <main className="flex-1 grid place-items-center px-4 py-12" data-testid="payment-return-page">
        {children}
      </main>
      <Footer />
    </div>
  );
}

function StatusCard({ icon, title, body, footer, accent, testid }) {
  const accents = {
    success: "bg-[#E7F4ED] text-[#1F7A3D] border-[#1F7A3D]/20",
    pending: "bg-[#FFF7E6] text-[#7A5A1B] border-[#F5C76E]/40",
    failed: "bg-[#FDECEC] text-[#9b1f2c] border-[#9b1f2c]/20",
    cancel: "bg-[#EDEEF0] text-[#373A45] border-[#E5E7EB]",
  };
  return (
    <article
      className="w-full max-w-xl rounded-3xl bg-white border border-[#E5E7EB] shadow-sm p-8 md:p-12 text-center"
      data-testid={testid}
    >
      <div className={`mx-auto w-16 h-16 rounded-full grid place-items-center border ${accents[accent]}`}>
        {icon}
      </div>
      <h1 className="mt-6 text-2xl md:text-3xl font-semibold text-[#373A45]" data-testid={`${testid}-title`}>
        {title}
      </h1>
      <div className="mt-3 text-sm md:text-base text-[#6B7280] leading-relaxed">{body}</div>
      {footer && <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">{footer}</div>}
    </article>
  );
}

function PrimaryButton({ to, onClick, children, testid }) {
  const cls =
    "inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold bg-[#965EA7] text-white hover:bg-[#7d4990] transition-colors";
  if (to) {
    return (
      <Link to={to} className={cls} data-testid={testid}>
        {children}
      </Link>
    );
  }
  return (
    <button onClick={onClick} className={cls} data-testid={testid}>
      {children}
    </button>
  );
}

function SecondaryButton({ to, onClick, children, testid }) {
  const cls =
    "inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold border border-[#E5E7EB] text-[#373A45] hover:bg-[#EDEEF0] transition-colors";
  if (to) {
    return (
      <Link to={to} className={cls} data-testid={testid}>
        {children}
      </Link>
    );
  }
  return (
    <button onClick={onClick} className={cls} data-testid={testid}>
      {children}
    </button>
  );
}

function formatAmount(amount, currency) {
  if (amount == null) return "";
  try {
    return `${Number(amount).toLocaleString("ro-RO")} ${currency || ""}`.trim();
  } catch {
    return `${amount} ${currency || ""}`;
  }
}

/**
 * Polls the backend for the current payment status. Stops when the
 * status becomes terminal (paid / failed / cancelled) or after
 * `POLL_MAX_ATTEMPTS` attempts.
 */
function usePaymentStatus(orderId, variant) {
  const [data, setData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  useEffect(() => {
    if (!orderId) {
      setError("missing_order_id");
      return undefined;
    }
    let cancelled = false;
    let attempt = 0;

    const tick = async () => {
      if (cancelled) return;
      attempt += 1;
      setAttempts(attempt);
      try {
        const r = await api.get(`/payments/status/${encodeURIComponent(orderId)}`);
        if (cancelled) return;
        setData(r.data);
        const s = r.data?.status;
        // Terminal states stop polling.
        if (s === "paid" || s === "failed" || s === "cancelled") return;
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 404) {
          // Backend may not have stored the order yet (cold-start race).
          // Keep polling unless we hit the cap.
        } else {
          setError("network");
        }
      }
      if (attempt < POLL_MAX_ATTEMPTS && variant === "success") {
        setTimeout(tick, POLL_INTERVAL_MS);
      }
    };

    tick();
    return () => { cancelled = true; };
  }, [orderId, variant]);

  return { data, attempts, error };
}

export default function PaymentReturn({ variant = "success" }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderId = params.get("order_id") || params.get("orderID");
  const { data, attempts, error } = usePaymentStatus(orderId, variant);

  // Fire the purchase event ONCE per order_id when status flips to paid.
  useEffect(() => {
    if (variant !== "success") return;
    if (!data || data.status !== "paid") return;
    const key = PURCHASE_TRACK_KEY(orderId);
    let already = false;
    try { already = localStorage.getItem(key) === "1"; } catch (_) {
      try { already = sessionStorage.getItem(key) === "1"; } catch (_) { /* noop */ }
    }
    if (already) return;
    try {
      // One object, matching `trackPurchase`'s signature. It used to be called
      // with (purchase, value, currency, eventId) — an older signature — so the
      // amount landed nowhere and every Purchase reached Meta worth 0 lei, on
      // the page every card payer comes back to. `purchase_id` is also the
      // dedup key the server sends with its own Purchase event.
      trackPurchase({
        id: data.purchase_id || orderId,
        amount: data.amount,
        currency: data.currency,
      });
    } finally {
      try { localStorage.setItem(key, "1"); } catch (_) {
        try { sessionStorage.setItem(key, "1"); } catch (_) { /* noop */ }
      }
    }
  }, [data, variant, orderId]);

  // ---- Variant: /payment/cancel ----
  if (variant === "cancel") {
    return (
      <PaymentLayout>
        <StatusCard
          accent="cancel"
          testid="payment-cancel-card"
          icon={<AlertCircle size={32} />}
          title="Plata a fost anulată"
          body={
            <p>
              Ai întrerupt procesul de plată înainte de finalizare. Înscrierea
              ta nu a fost confirmată. Poți reîncerca oricând.
            </p>
          }
          footer={
            <>
              <PrimaryButton to="/checkout" testid="payment-cancel-retry-btn">
                <ArrowLeft size={16} /> Reia plata
              </PrimaryButton>
              <SecondaryButton to="/" testid="payment-cancel-home-btn">
                Înapoi la pagina principală
              </SecondaryButton>
            </>
          }
        />
      </PaymentLayout>
    );
  }

  // ---- Variant: /payment/failed ----
  if (variant === "failed") {
    return (
      <PaymentLayout>
        <StatusCard
          accent="failed"
          testid="payment-failed-card"
          icon={<XCircle size={32} />}
          title="Plata nu a putut fi procesată"
          body={
            <>
              <p>
                Banca a refuzat tranzacția sau aceasta a fost întreruptă.
                Sumele nu au fost debitate.
              </p>
              <p className="mt-2 text-xs">
                Cauze frecvente: card expirat, limită depășită, 3-D Secure
                eșuat. Te rugăm să verifici detaliile cardului și să încerci
                din nou.
              </p>
            </>
          }
          footer={
            <>
              <PrimaryButton to="/checkout" testid="payment-failed-retry-btn">
                Încearcă din nou
              </PrimaryButton>
              <SecondaryButton
                onClick={() => { window.location.href = "mailto:contact@fped.org?subject=Probleme%20plata%20curs%20AI"; }}
                testid="payment-failed-contact-btn"
              >
                <Mail size={16} /> Contactează-ne
              </SecondaryButton>
            </>
          }
        />
      </PaymentLayout>
    );
  }

  // ---- Variant: /payment/success — status-driven ----
  // Step 1: no order_id in the URL → corrupted return.
  if (!orderId) {
    return (
      <PaymentLayout>
        <StatusCard
          accent="failed"
          testid="payment-success-missing-id"
          icon={<AlertCircle size={32} />}
          title="Nu am putut identifica comanda"
          body={<p>Lipsește identificatorul comenzii. Te rugăm să ne contactezi pentru ajutor.</p>}
          footer={<PrimaryButton to="/" testid="payment-success-missing-home">Înapoi la pagina principală</PrimaryButton>}
        />
      </PaymentLayout>
    );
  }

  // Step 2: terminal failed or cancelled state from backend → render the
  // failed/cancel variants in-place (no redirect — preserves order_id).
  const status = data?.status;
  if (status === "failed") {
    return (
      <PaymentLayout>
        <StatusCard
          accent="failed"
          testid="payment-success-as-failed"
          icon={<XCircle size={32} />}
          title="Plata nu a putut fi procesată"
          body={<p>Tranzacția a eșuat. Sumele nu au fost debitate. Reîncearcă mai jos.</p>}
          footer={
            <PrimaryButton to="/checkout" testid="payment-success-retry-btn">
              Încearcă din nou
            </PrimaryButton>
          }
        />
      </PaymentLayout>
    );
  }
  if (status === "cancelled") {
    return (
      <PaymentLayout>
        <StatusCard
          accent="cancel"
          testid="payment-success-as-cancelled"
          icon={<AlertCircle size={32} />}
          title="Plata a fost anulată"
          body={<p>Ai anulat procesul de plată. Poți reîncerca oricând.</p>}
          footer={
            <PrimaryButton to="/checkout" testid="payment-success-cancelled-retry">
              Reia plata
            </PrimaryButton>
          }
        />
      </PaymentLayout>
    );
  }

  // Step 3: paid → success copy.
  if (status === "paid") {
    return (
      <PaymentLayout>
        <StatusCard
          accent="success"
          testid="payment-success-paid"
          icon={<CheckCircle2 size={32} />}
          title="Plata a fost procesată cu succes!"
          body={
            <>
              <p>
                Mulțumim! Înscrierea ta la cursul <strong>„Introducere în
                Inteligența Artificială pentru educație"</strong> este
                confirmată.
              </p>
              <p className="mt-3 text-xs">
                Suma plătită:{" "}
                <strong className="text-[#373A45]">{formatAmount(data?.amount, data?.currency)}</strong> ·
                {" "}Comandă:{" "}
                <span className="font-mono text-xs text-[#373A45]">#{orderId.slice(-8)}</span>
              </p>
              <p className="mt-3">
                Ți-am trimis un email de confirmare cu toate detaliile despre
                grupă, calendar și accesul la platformă.
              </p>
            </>
          }
          footer={
            <>
              {data?.enrollment_id && (
                <PrimaryButton
                  to={`/success?enrollment_id=${data.enrollment_id}&mode=paid`}
                  testid="payment-success-details-btn"
                >
                  Vezi detalii înscriere <ArrowRight size={16} />
                </PrimaryButton>
              )}
              <SecondaryButton to="/" testid="payment-success-home-btn">
                Înapoi la pagina principală
              </SecondaryButton>
            </>
          }
        />
      </PaymentLayout>
    );
  }

  // Step 4: pending (or no data yet) → "we're confirming".
  // After POLL_MAX_ATTEMPTS we soften the copy: confirmation takes longer
  // sometimes, but the IPN will eventually arrive. The user can refresh.
  const exhausted = attempts >= POLL_MAX_ATTEMPTS;
  return (
    <PaymentLayout>
      <StatusCard
        accent="pending"
        testid="payment-success-pending"
        icon={<Loader2 className="animate-spin" size={32} />}
        title={exhausted ? "Confirmăm plata" : "Confirmăm plata cu banca…"}
        body={
          <>
            <p>
              Tranzacția ta a fost trimisă către procesatorul de plăți.
              Așteptăm confirmarea finală — durează de obicei câteva secunde.
            </p>
            {exhausted ? (
              <p className="mt-3 text-xs">
                Dacă banca îți confirmă plata pe extras dar pagina noastră încă
                pare blocată, te rugăm să ne scrii la{" "}
                <a className="underline" href="mailto:contact@fped.org">contact@fped.org</a>{" "}
                cu numărul comenzii{" "}
                <span className="font-mono">#{orderId.slice(-8)}</span>{" "}
                și vom corela manual înscrierea în maximum 1 oră.
              </p>
            ) : (
              <p className="mt-3 text-xs">Verificare #{attempts}/{POLL_MAX_ATTEMPTS}…</p>
            )}
            {error === "network" && (
              <p className="mt-3 text-xs text-[#9b1f2c]">
                Probleme la conectarea cu serverul. Reîncearcă reîncărcarea
                paginii.
              </p>
            )}
          </>
        }
        footer={
          exhausted && (
            <>
              <PrimaryButton onClick={() => navigate(0)} testid="payment-success-refresh-btn">
                Reîncarcă pagina
              </PrimaryButton>
              <SecondaryButton to="/" testid="payment-success-pending-home-btn">
                Înapoi la pagina principală
              </SecondaryButton>
            </>
          )
        }
      />
    </PaymentLayout>
  );
}
